import { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/common/Logo";
import { PhoneInput } from "@/components/common/PhoneInput";
import { DEFAULT_COUNTRY, parsePhone, toE164, type Country } from "@/lib/countries";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { differenceInYears, parseISO } from "date-fns";
import { useQueryClient } from "@tanstack/react-query";

const REF_KEY = "super-pari-ref";

const phoneShape = z.object({
  country: z.object({ code: z.string(), name: z.string(), dial: z.string(), flag: z.string() }),
  local: z.string().min(6, "Numéro trop court").max(15, "Numéro trop long"),
});

const schema = z
  .object({
    first_name: z.string().min(2, "Prénom trop court"),
    phone: phoneShape,
    email: z.string().email("Email invalide").optional().or(z.literal("")),
    dob: z.string().min(1, "Date requise"),
    age_confirmed: z.literal(true, { errorMap: () => ({ message: "Tu dois cocher la case" }) }),
  })
  .refine((d) => differenceInYears(new Date(), parseISO(d.dob)) >= 18, {
    path: ["dob"],
    message: "Tu dois avoir 18 ans ou plus",
  });

type FormValues = z.infer<typeof schema>;

export function OnboardingProfile() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [loading, setLoading] = useState(false);

  const initialPhone = parsePhone(user?.phone ?? null);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      email: user?.email ?? "",
      first_name: extractFirstName(
        (user?.user_metadata?.full_name as string | undefined) ??
          (user?.user_metadata?.name as string | undefined) ??
          ""
      ),
      phone: { country: initialPhone.country ?? DEFAULT_COUNTRY, local: initialPhone.local },
    },
  });

  async function onSubmit(data: FormValues) {
    if (!user) return;
    setLoading(true);
    try {
      const e164 = toE164(data.phone.country as Country, data.phone.local);

      const { error } = await supabase
        .from("users")
        .update({
          first_name: data.first_name.trim(),
          email: data.email?.trim() || null,
          dob: data.dob,
          phone: e164,
        })
        .eq("id", user.id);
      if (error) throw error;

      const refCode = localStorage.getItem(REF_KEY);
      if (refCode) {
        await supabase.functions
          .invoke("auth-bind-referrer", { body: { code: refCode } })
          .catch(() => null);
        localStorage.removeItem(REF_KEY);
      }

      await qc.invalidateQueries({ queryKey: ["profile"] });
      toast.success("Bienvenue !");
      navigate("/", { replace: true });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erreur");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen px-6 py-6 safe-top safe-bottom">
      <div className="flex justify-center pb-4">
        <Logo />
      </div>

      <h1 className="font-display text-2xl font-extrabold">{t("auth.onboardingTitle")}</h1>
      <p className="mt-1 text-muted-foreground">Quelques infos pour finaliser ton compte.</p>

      <form onSubmit={form.handleSubmit(onSubmit)} className="mt-6 space-y-4">
        <div className="space-y-2">
          <Label htmlFor="first_name">{t("auth.firstName")} *</Label>
          <Input id="first_name" autoComplete="given-name" {...form.register("first_name")} />
          {form.formState.errors.first_name && (
            <p className="text-sm text-destructive">{form.formState.errors.first_name.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label>Numéro de téléphone *</Label>
          <Controller
            control={form.control}
            name="phone"
            render={({ field }) => (
              <PhoneInput
                value={field.value as { country: Country; local: string }}
                onChange={field.onChange}
              />
            )}
          />
          <p className="text-xs text-muted-foreground">Utilisé pour les recharges et retraits mobile money.</p>
          {form.formState.errors.phone?.local && (
            <p className="text-sm text-destructive">{form.formState.errors.phone.local.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="email">{t("auth.email")}</Label>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            disabled={!!user?.email}
            {...form.register("email")}
          />
          {form.formState.errors.email && (
            <p className="text-sm text-destructive">{form.formState.errors.email.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="dob">{t("auth.dob")} *</Label>
          <Input id="dob" type="date" {...form.register("dob")} />
          {form.formState.errors.dob && (
            <p className="text-sm text-destructive">{form.formState.errors.dob.message}</p>
          )}
        </div>

        <label className="flex items-start gap-3 rounded-lg border bg-card p-3 cursor-pointer">
          <input
            type="checkbox"
            className="mt-0.5 size-5 accent-primary"
            {...form.register("age_confirmed")}
          />
          <span className="text-sm leading-relaxed">
            {t("auth.ageConfirm")}{" "}
            <a href="/legal/terms" target="_blank" className="underline text-primary">
              {t("auth.termsLink")}
            </a>
          </span>
        </label>
        {form.formState.errors.age_confirmed && (
          <p className="text-sm text-destructive">{form.formState.errors.age_confirmed.message}</p>
        )}

        <Button type="submit" loading={loading} size="xl" className="w-full">
          {t("common.confirm")}
        </Button>
      </form>
    </div>
  );
}

function extractFirstName(fullName: string): string {
  if (!fullName) return "";
  return fullName.trim().split(/\s+/)[0] ?? "";
}
